/**
 * Sync conflict resolver.
 *
 * Shows one conflict at a time (with a N-of-M counter): mine on the left,
 * the server copy on the right, three ways out. The engine guarantees the
 * local edit is intact until one of the strategies is chosen — nothing here
 * can silently drop data.
 */
import { useState } from 'react';
import { GitMerge } from 'lucide-react';
import type { NoteRecord } from '../lib/sync/dto.js';
import type { SyncConflict } from '../lib/sync/v2.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

export type ResolveChoice = 'keep_mine' | 'take_theirs' | 'fork_copy';

interface ConflictDialogProps {
  conflicts: SyncConflict[];
  /** Finds the local (edited) record for a conflict id. */
  localNote: (id: string) => NoteRecord | null;
  onResolve: (conflict: SyncConflict, choice: ResolveChoice) => Promise<void>;
  onClose: () => void;
}

export function ConflictDialog({ conflicts, localNote, onResolve, onClose }: ConflictDialogProps) {
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const conflict = conflicts[index];
  // Floating capsule scrollbars (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const modalScroll = useOverlayScrollbar<HTMLDivElement>();
  const mineScroll = useOverlayScrollbar<HTMLPreElement>();
  const theirsScroll = useOverlayScrollbar<HTMLPreElement>();

  if (!conflict) return null;
  const mine = conflict.kind === 'note' ? (localNote(conflict.id) ?? null) : null;
  const theirs = conflict.kind === 'note' && conflict.theirs && 'content' in conflict.theirs
    ? (conflict.theirs as NoteRecord)
    : null;

  async function choose(choice: ResolveChoice): Promise<void> {
    setBusy(true);
    try {
      await onResolve(conflict, choice);
      // Step to the next conflict; the dialog closes when the list empties.
      setIndex((current) => Math.min(current, Math.max(conflicts.length - 2, 0)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="ob-host ob-host-modal" ref={modalScroll.hostRef}>
        <div
          className="modal modal-wide"
          role="dialog"
          aria-label="Resolve sync conflict"
          onClick={(event) => event.stopPropagation()}
          ref={modalScroll.scrollerRef}
        >
        <div className="modal-header">
          <GitMerge size={15} strokeWidth={1.75} />
          <span>
            Resolve sync conflict{conflicts.length > 1 ? ` (${index + 1} of ${conflicts.length})` : ''}
          </span>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close" disabled={busy}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {conflict.kind === 'note' ? (
            <>
              <p className="modal-text">
                This note was edited on another device after your last sync. Both versions are
                preserved — pick which one keeps the title, or keep both as separate notes.
              </p>
              <div className="conflict-grid">
                <section className="conflict-pane">
                  <header>
                    <strong>Your version</strong>
                    <span>{mine ? `edited ${mine.updatedAt.toLocaleString()}` : 'missing'}</span>
                  </header>
                  <h3>{mine?.title ?? '(deleted here)'}</h3>
                  <div className="ob-host ob-host-pre" ref={mineScroll.hostRef}>
                    <pre ref={mineScroll.scrollerRef}>{mine?.content ?? ''}</pre>
                  </div>
                </section>
                <section className="conflict-pane">
                  <header>
                    <strong>On the server</strong>
                    <span>
                      {theirs ? `edited ${new Date(theirs.updatedAt).toLocaleString()}` : 'missing'}
                    </span>
                  </header>
                  <h3>{theirs?.title ?? '(deleted remotely)'}</h3>
                  <div className="ob-host ob-host-pre" ref={theirsScroll.hostRef}>
                    <pre ref={theirsScroll.scrollerRef}>{theirs?.content ?? ''}</pre>
                  </div>
                </section>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="button button-quiet button-small"
                  disabled={busy}
                  onClick={() => void choose('take_theirs')}
                >
                  Take server version
                </button>
                <button
                  type="button"
                  className="button button-quiet button-small"
                  disabled={busy}
                  onClick={() => void choose('fork_copy')}
                >
                  Keep both as copies
                </button>
                <button
                  type="button"
                  className="button button-small"
                  disabled={busy}
                  onClick={() => void choose('keep_mine')}
                >
                  Keep my version
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="modal-text">
                Folder “{conflict.theirs && 'name' in conflict.theirs ? conflict.theirs.name : conflict.id}”
                changed on another device. Keep your version, or take the server&apos;s.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="button button-quiet button-small"
                  disabled={busy}
                  onClick={() => void choose('take_theirs')}
                >
                  Take server version
                </button>
                <button
                  type="button"
                  className="button button-small"
                  disabled={busy}
                  onClick={() => void choose('keep_mine')}
                >
                  Keep my version
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
